import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  giftCouponRedemptionListDataAtom,
  giftCouponRedemptionListStatusAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import { deliverCoupon } from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponRedemptionListHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';

// 상태별 뱃지 스타일
const getStatusBadge = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Requested:
      return { text: '신청됨', className: 'bg-yellow-100 text-yellow-800' };
    case GiftCouponRedeemStatus.Delivered:
      return { text: '발송완료', className: 'bg-green-100 text-green-800' };
    case GiftCouponRedeemStatus.Expired:
      return { text: '만료', className: 'bg-gray-100 text-gray-800' };
    case GiftCouponRedeemStatus.Refunded:
      return { text: '환불', className: 'bg-red-100 text-red-800' };
    case GiftCouponRedeemStatus.Canceled:
      return { text: '취소', className: 'bg-gray-100 text-gray-500' };
    default:
      return { text: '대기', className: 'bg-blue-100 text-blue-800' };
  }
};

// 신청일시 포맷팅 (년월일 + 시간)
const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
  });
};

// 만료일 포맷팅 (년월일만)
const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const GiftCouponRedemptionListPage: FC = function GiftCouponRedemptionListPage() {
  // 데이터 조회
  useGiftCouponRedemptionListHook(LowerRole.Admin);
  const status = useRecoilValue(giftCouponRedemptionListStatusAtom);
  const [data, setData] = useRecoilState(giftCouponRedemptionListDataAtom);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [deliveringId, setDeliveringId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // 단일 발송 처리
  const handleDeliver = useCallback(
    async (couponId: string) => {
      if (!window.confirm('발송 처리하시겠습니까?')) return;

      setDeliveringId(couponId);
      try {
        const updated = await deliverCoupon(couponId);
        // 로컬 상태 업데이트
        setData((prev) =>
          prev.map((item) =>
            item._id === couponId
              ? { ...item, redeemStatus: updated.redeemStatus, gifticon: updated.gifticon }
              : item,
          ),
        );
        alert('발송 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송 처리 실패:', error);
        alert('발송 처리에 실패했습니다.');
      } finally {
        setDeliveringId(null);
      }
    },
    [setData],
  );

  // 일괄 발송 처리
  const handleBulkDeliver = useCallback(async () => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }

    if (!window.confirm(`선택된 ${targetIds.length}건을 발송 처리하시겠습니까?`)) return;

    setIsBulkProcessing(true);
    let successCount = 0;
    let failCount = 0;

    for (const couponId of targetIds) {
      try {
        const updated = await deliverCoupon(couponId);
        setData((prev) =>
          prev.map((item) =>
            item._id === couponId
              ? { ...item, redeemStatus: updated.redeemStatus, gifticon: updated.gifticon }
              : item,
          ),
        );
        successCount++;
      } catch (error) {
        console.error(`발송 처리 실패 (${couponId}):`, error);
        failCount++;
      }
    }

    setIsBulkProcessing(false);
    setSelectedIds(new Set());

    if (failCount === 0) {
      alert(`${successCount}건 발송 처리 완료`);
    } else {
      alert(`성공: ${successCount}건, 실패: ${failCount}건`);
    }
  }, [selectedIds, setData]);

  // 체크박스 토글
  const handleToggleSelect = useCallback((couponId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(couponId)) {
        newSet.delete(couponId);
      } else {
        newSet.add(couponId);
      }
      return newSet;
    });
  }, []);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    let result = [...data];

    // 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((item) => item.redeemStatus === statusFilter);
    }

    // 날짜 필터
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
      }
    }

    // 검색어 필터 (전화번호, 상품명)
    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const itemName =
          typeof item.giftItem === 'object'
            ? (item.giftItem?.name || item.giftItem?.giftItemInfo?.name || '').toLowerCase()
            : '';
        const unitName =
          item.selectedChoiceUnit && typeof item.selectedChoiceUnit === 'object'
            ? item.selectedChoiceUnit.name?.toLowerCase() || ''
            : '';
        return phone.includes(search) || itemName.includes(search) || unitName.includes(search);
      });
    }

    // 날짜순 정렬 (최신순)
    result.sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    return result;
  }, [data, statusFilter, dateFilter, searchText]);

  // 통계
  const stats = useMemo(() => {
    const requested = data.filter(
      (d) => d.redeemStatus === GiftCouponRedeemStatus.Requested,
    ).length;
    const delivered = data.filter(
      (d) => d.redeemStatus === GiftCouponRedeemStatus.Delivered,
    ).length;
    const total = data.length;
    return { requested, delivered, total };
  }, [data]);

  // 선택 가능한 항목들 (Requested 상태만)
  const selectableItems = useMemo(
    () => filteredData.filter((item) => item.redeemStatus === GiftCouponRedeemStatus.Requested),
    [filteredData],
  );

  // 전체 선택 여부
  const isAllSelected = useMemo(
    () => selectableItems.length > 0 && selectableItems.every((item) => selectedIds.has(item._id)),
    [selectableItems, selectedIds],
  );

  // 전체 선택/해제
  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      // 전체 해제
      setSelectedIds(new Set());
    } else {
      // 전체 선택
      setSelectedIds(new Set(selectableItems.map((item) => item._id)));
    }
  }, [isAllSelected, selectableItems]);

  // 전화번호 포맷팅
  const formatPhone = useCallback((phone: string | undefined) => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  }, []);

  // 상품명 가져오기
  const getItemName = useCallback((coupon: GiftCouponForStore) => {
    if (typeof coupon.giftItem === 'object') {
      return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
    }
    return '-';
  }, []);

  // 선택 유닛명 가져오기
  const getChoiceUnitName = useCallback((coupon: GiftCouponForStore) => {
    if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
      return coupon.selectedChoiceUnit.name;
    }
    return null;
  }, []);

  // 가격 정보 가져오기
  const getPriceInfo = useCallback((coupon: GiftCouponForStore) => {
    if (typeof coupon.giftItem === 'object') {
      return {
        priceRegular: coupon.giftItem?.priceRegular || 0,
        priceRetail: coupon.giftItem?.priceRetail || 0,
        priceWhole: coupon.giftItem?.priceWhole || 0,
        priceRebate: coupon.giftItem?.priceRebate || 0,
      };
    }
    return { priceRegular: 0, priceRetail: 0, priceWhole: 0, priceRebate: 0 };
  }, []);

  // 이미지 URL 가져오기
  const getImageUrl = useCallback((coupon: GiftCouponForStore) => {
    // 선택 유닛 이미지 우선
    if (
      coupon.selectedChoiceUnit &&
      typeof coupon.selectedChoiceUnit === 'object' &&
      coupon.selectedChoiceUnit.image
    ) {
      const img = coupon.selectedChoiceUnit.image;
      const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    // 상품 이미지
    if (typeof coupon.giftItem === 'object') {
      // giftItem.image 먼저
      const giftItemImg = coupon.giftItem?.image;
      if (giftItemImg) {
        const imgUrl = typeof giftItemImg === 'object' && 'url' in giftItemImg ? giftItemImg.url : giftItemImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
      // giftItemInfo.image
      const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
      if (giftItemInfoImg) {
        const imgUrl = typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg ? giftItemInfoImg.url : giftItemInfoImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
    }
    return '';
  }, []);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <h1 className="text-xl font-bold">쿠폰 교환 신청 확인</h1>
        <p className="mt-1 text-sm text-gray-500">
          교환 신청된 쿠폰을 확인하고 기프티콘을 발송하세요.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 p-4">
        <div className="rounded-lg bg-yellow-50 p-4 text-center">
          <p className="text-2xl font-bold text-yellow-700">{stats.requested}</p>
          <p className="text-sm text-yellow-600">발송 대기</p>
        </div>
        <div className="rounded-lg bg-green-50 p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
          <p className="text-sm text-green-600">발송 완료</p>
        </div>
        <div className="rounded-lg bg-gray-100 p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
          <p className="text-sm text-gray-600">전체</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* 상태 필터 */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            <option value={GiftCouponRedeemStatus.Requested}>신청됨 (발송대기)</option>
            <option value={GiftCouponRedeemStatus.Delivered}>발송완료</option>
            <option value={GiftCouponRedeemStatus.Expired}>만료</option>
            <option value={GiftCouponRedeemStatus.Refunded}>환불</option>
          </select>

          {/* 날짜 필터 */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 기간</option>
            <option value="today">오늘</option>
            <option value="week">최근 1주</option>
            <option value="month">최근 1개월</option>
          </select>

          {/* 검색 */}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="전화번호, 상품명 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {/* 일괄 처리 영역 */}
        {selectedIds.size > 0 && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
            <span className="text-sm font-medium text-indigo-700">
              {selectedIds.size}건 선택됨
            </span>
            <button
              onClick={handleBulkDeliver}
              disabled={isBulkProcessing}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBulkProcessing ? '처리 중...' : '일괄 발송처리'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              선택 해제
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        {filteredData.length > 0 ? (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      disabled={selectableItems.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    상태
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    상품
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    정가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    판매가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    도매가
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    수신자 정보
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    신청일시
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    만료일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredData.map((coupon) => {
                  const badge = getStatusBadge(coupon.redeemStatus);
                  const choiceUnitName = getChoiceUnitName(coupon);
                  const priceInfo = getPriceInfo(coupon);

                  const isSelectable = coupon.redeemStatus === GiftCouponRedeemStatus.Requested;
                  const isSelected = selectedIds.has(coupon._id);

                  return (
                    <tr
                      key={coupon._id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                    >
                      {/* 체크박스 */}
                      <td className="px-4 py-3">
                        {isSelectable ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(coupon._id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        ) : (
                          <span className="block h-4 w-4" />
                        )}
                      </td>

                      {/* 상태 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badge.className}`}
                        >
                          {badge.text}
                        </span>
                      </td>

                      {/* 상품 */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {getImageUrl(coupon) && (
                            <img
                              src={getImageUrl(coupon)}
                              alt=""
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {getItemName(coupon)}
                            </p>
                            {choiceUnitName && (
                              <p className="text-xs text-indigo-600">→ {choiceUnitName}</p>
                            )}
                            <p className="text-xs text-gray-400">#{coupon.fullCode}</p>
                          </div>
                        </div>
                      </td>

                      {/* 정가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-500">
                        {priceInfo.priceRegular.toLocaleString()}원
                      </td>

                      {/* 판매가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {priceInfo.priceRetail.toLocaleString()}원
                      </td>

                      {/* 도매가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-indigo-600">
                        {priceInfo.priceWhole.toLocaleString()}원
                      </td>

                      {/* 수신자 정보 */}
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {formatPhone(coupon.recipient?.phoneNumber)}
                        </p>
                        {coupon.recipient?.name && (
                          <p className="text-xs text-gray-500">{coupon.recipient.name}</p>
                        )}
                      </td>

                      {/* 신청일시 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDateTime(coupon.recipient?.requestedAt)}
                      </td>

                      {/* 만료일 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(coupon.expiresAt)}
                      </td>

                      {/* 액션 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Requested && (
                          <button
                            onClick={() => handleDeliver(coupon._id)}
                            disabled={deliveringId === coupon._id}
                            className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deliveringId === coupon._id ? '처리중...' : '발송처리'}
                          </button>
                        )}
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered && (
                          <span className="text-xs text-gray-400">
                            {formatDateTime(coupon.gifticon?.deliveredAt)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <p className="text-gray-500">조건에 맞는 교환 신청이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};
